import React, { useCallback, useState } from 'react';

// Admin → Enterprise accounts (backend: /api/admin/enterprise, migration 216).
// Set someone up on a plan with no monthly fee and no end date, and choose
// what their monitoring and job vacancy add-ons and contact credits cost —
// each one either the normal price or this account's own.

interface AddOn { quota: number; pricePence: number }
interface Tier { key: string; name: string; monthlyPricePence: number; monitoring: AddOn | null; jobs: AddOn | null }
interface Account {
  userId: number; email: string; name: string; company: string | null;
  tierKey: string | null; tierName: string | null; planStatus: string | null; teamSize: number;
  includedMonitoringChecks: number; endsAt: string | null;
  monitoringPencePerCheck: number | null; contactPencePerCredit: number | null; jobsBundlePence: number | null;
  note: string | null; createdAt: string;
}

// Normal contact credit price, for comparison (routes/credits.js: 5,000 = £299).
const NORMAL_CONTACT_PENCE = 29900 / 5000;

const gbp = (pence: number) => `£${(pence / 100).toLocaleString('en-GB', { minimumFractionDigits: pence % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
const num = (n: number) => n.toLocaleString('en-GB');

interface Form {
  email: string; fullName: string; tierKey: string; includedChecks: string;
  monitoringCustom: boolean; monitoringPounds: string;
  jobsCustom: boolean; jobsPounds: string;
  contactCustom: boolean; contactPence: string;
  ends: boolean; endsOn: string; // ends = has an end date; endsOn = YYYY-MM-DD
  note: string;
}
const EMPTY: Form = {
  email: '', fullName: '', tierKey: 'scale', includedChecks: '0',
  monitoringCustom: false, monitoringPounds: '', jobsCustom: false, jobsPounds: '',
  contactCustom: false, contactPence: '', ends: false, endsOn: '', note: '',
};

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const today = () => new Date().toISOString().slice(0, 10);

export const EnterprisePanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [form, setForm] = useState<Form>(EMPTY);
  const [editing, setEditing] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const auth = { Authorization: `Bearer ${window.localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await fetch('/api/admin/enterprise', { headers: auth });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Could not load enterprise accounts'); return; }
      setTiers(d.tiers || []); setAccounts(d.accounts || []);
    } catch { setError('Could not load enterprise accounts'); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => { if (!open) load(); setOpen(!open); };
  const set = (patch: Partial<Form>) => { setForm((f) => ({ ...f, ...patch })); setConfirming(false); setDone(null); };
  const tier = tiers.find((t) => t.key === form.tierKey) || null;

  const edit = (a: Account) => {
    const t = tiers.find((x) => x.key === a.tierKey);
    setEditing(a.userId); setDone(null); setConfirming(false); setError(null);
    setForm({
      email: a.email, fullName: '', tierKey: a.tierKey || 'scale', includedChecks: String(a.includedMonitoringChecks || 0),
      monitoringCustom: a.monitoringPencePerCheck != null,
      monitoringPounds: a.monitoringPencePerCheck != null && t?.monitoring ? ((a.monitoringPencePerCheck * t.monitoring.quota) / 100).toFixed(2) : '',
      jobsCustom: a.jobsBundlePence != null, jobsPounds: a.jobsBundlePence != null ? (a.jobsBundlePence / 100).toFixed(2) : '',
      contactCustom: a.contactPencePerCredit != null, contactPence: a.contactPencePerCredit != null ? String(a.contactPencePerCredit) : '',
      ends: !!a.endsAt, endsOn: a.endsAt ? String(a.endsAt).slice(0, 10) : '',
      note: a.note || '',
    });
  };

  const pounds = (s: string) => (s.trim() === '' ? NaN : Math.round(Number(s) * 100));
  const problems: string[] = [];
  if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) problems.push('an email');
  if (form.monitoringCustom && !(pounds(form.monitoringPounds) > 0)) problems.push('a monitoring price above £0 (to give checks free, include them each month)');
  if (form.jobsCustom && !(pounds(form.jobsPounds) > 0)) problems.push('a job vacancy price above £0');
  if (form.contactCustom && !(Number(form.contactPence) > 0)) problems.push('a contact credit price');
  if (form.ends && !(form.endsOn && form.endsOn >= today())) problems.push('an end date from today on');

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const r = await fetch('/api/admin/enterprise', {
        method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email.trim(), fullName: form.fullName.trim() || null, tierKey: form.tierKey,
          includedMonitoringChecks: parseInt(form.includedChecks, 10) || 0,
          monitoringBundlePence: form.monitoringCustom ? pounds(form.monitoringPounds) : null,
          jobsBundlePence: form.jobsCustom ? pounds(form.jobsPounds) : null,
          contactPencePerCredit: form.contactCustom ? Number(form.contactPence) : null,
          endsOn: form.ends ? form.endsOn : null,
          note: form.note.trim() || null,
        }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Could not set up the account'); setConfirming(false); }
      else {
        setDone(`${d.email} is on ${d.tier} Enterprise${d.endsAt ? ` until ${fmtDate(d.endsAt)}` : ', no end date'}${d.planUnchanged ? ' (changes saved)' : ''}.${d.created ? ' A set-password email has gone to them.' : ' No email was sent.'}`);
        setForm(EMPTY); setEditing(null); setConfirming(false); load();
      }
    } catch { setError('Could not set up the account'); }
    setBusy(false);
  };

  const priceLine = (a: Account) => {
    const t = tiers.find((x) => x.key === a.tierKey);
    const mon = a.monitoringPencePerCheck != null && t?.monitoring ? `${gbp(Math.round(a.monitoringPencePerCheck * t.monitoring.quota))} / +${num(t.monitoring.quota)} checks` : 'normal';
    const jobs = a.jobsBundlePence != null && t?.jobs ? `${gbp(a.jobsBundlePence)} / +${num(t.jobs.quota)} jobs` : 'normal';
    const con = a.contactPencePerCredit != null ? `${a.contactPencePerCredit}p per credit` : 'normal';
    return { mon, jobs, con };
  };

  const radio = (on: boolean, label: React.ReactNode, onPick: () => void) => (
    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
      <input type="radio" checked={on} onChange={onPick} className="text-violet-600" />
      {label}
    </label>
  );

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <button onClick={toggle} className="w-full p-6 flex justify-between items-center text-left">
        <div>
          <h2 className="text-xl font-semibold">Enterprise accounts</h2>
          <p className="text-sm text-gray-500 mt-1">No monthly fee, no end date — set each account's add-on prices</p>
        </div>
        <span className="text-sm text-blue-600">{open ? 'Hide' : 'Open'}</span>
      </button>

      {open && (
        <div className="px-6 pb-6 space-y-6">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">{error}</div>}
          {done && <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-900">{done}</div>}

          {/* The form */}
          <div className="border border-violet-200 rounded-lg p-5 bg-violet-50/40 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">{editing ? `Change ${form.email}` : 'Set someone up'}</h3>
              {editing && <button className="text-sm text-gray-500 hover:text-gray-800" onClick={() => { setEditing(null); setForm(EMPTY); setConfirming(false); }}>Cancel</button>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block text-sm">
                <span className="text-gray-700 font-medium">Email</span>
                <input className="mt-1 w-full border border-gray-300 rounded px-3 py-2" value={form.email} disabled={!!editing}
                  onChange={(e) => set({ email: e.target.value })} placeholder="name@company.com" />
              </label>
              {!editing && (
                <label className="block text-sm">
                  <span className="text-gray-700 font-medium">Full name</span>
                  <span className="text-gray-400"> — only if they don't have an account yet</span>
                  <input className="mt-1 w-full border border-gray-300 rounded px-3 py-2" value={form.fullName}
                    onChange={(e) => set({ fullName: e.target.value })} placeholder="First Last" />
                </label>
              )}
              <label className="block text-sm">
                <span className="text-gray-700 font-medium">Plan</span>
                <select className="mt-1 w-full border border-gray-300 rounded px-3 py-2" value={form.tierKey} onChange={(e) => set({ tierKey: e.target.value })}>
                  {tiers.map((t) => <option key={t.key} value={t.key}>{t.name} (normally {gbp(t.monthlyPricePence)}/month) — free</option>)}
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-gray-700 font-medium">Monitoring checks included each month</span>
                <input type="number" min={0} step={500} className="mt-1 w-full border border-gray-300 rounded px-3 py-2" value={form.includedChecks}
                  onChange={(e) => set({ includedChecks: e.target.value })} />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Monitoring add-on {tier?.monitoring && <span className="text-gray-400 font-normal">(+{num(tier.monitoring.quota)} checks/month)</span>}</p>
                {radio(!form.monitoringCustom, <>Normal price{tier?.monitoring && <> — {gbp(tier.monitoring.pricePence)}/month</>}</>, () => set({ monitoringCustom: false }))}
                {radio(form.monitoringCustom, 'Their own price', () => set({ monitoringCustom: true }))}
                {form.monitoringCustom && (
                  <div className="flex items-center gap-1 pl-6 text-sm">£<input className="w-24 border border-gray-300 rounded px-2 py-1" inputMode="decimal" value={form.monitoringPounds}
                    onChange={(e) => set({ monitoringPounds: e.target.value })} placeholder="25.00" /> /month</div>
                )}
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Job vacancy add-on {tier?.jobs && <span className="text-gray-400 font-normal">(+{num(tier.jobs.quota)} jobs/month)</span>}</p>
                {radio(!form.jobsCustom, <>Normal price{tier?.jobs && <> — {gbp(tier.jobs.pricePence)}/month</>}</>, () => set({ jobsCustom: false }))}
                {radio(form.jobsCustom, 'Their own price', () => set({ jobsCustom: true }))}
                {form.jobsCustom && (
                  <div className="flex items-center gap-1 pl-6 text-sm">£<input className="w-24 border border-gray-300 rounded px-2 py-1" inputMode="decimal" value={form.jobsPounds}
                    onChange={(e) => set({ jobsPounds: e.target.value })} placeholder="50.00" /> /month</div>
                )}
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Contact credits</p>
                {radio(!form.contactCustom, <>Normal price — 5,000 for {gbp(Math.round(NORMAL_CONTACT_PENCE * 5000))}</>, () => set({ contactCustom: false }))}
                {radio(form.contactCustom, 'Their own price', () => set({ contactCustom: true }))}
                {form.contactCustom && (
                  <div className="pl-6 text-sm">
                    <div className="flex items-center gap-1"><input className="w-20 border border-gray-300 rounded px-2 py-1" inputMode="decimal" value={form.contactPence}
                      onChange={(e) => set({ contactPence: e.target.value })} placeholder="2.7" /> p per credit</div>
                    {Number(form.contactPence) > 0 && <p className="text-xs text-gray-500 mt-1">5,000 = {gbp(Math.round(Number(form.contactPence) * 5000))}</p>}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Ends</p>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                {radio(!form.ends, 'No end date', () => set({ ends: false }))}
                {radio(form.ends, 'On a date', () => set({ ends: true }))}
                {form.ends && (
                  <input type="date" min={today()} className="border border-gray-300 rounded px-2 py-1 text-sm" value={form.endsOn}
                    onChange={(e) => set({ endsOn: e.target.value })} />
                )}
              </div>
              {form.ends && <p className="text-xs text-gray-500">Full access through the end of that day; then Search, monitoring and Claude switch off. You get a notice and an email 14 days before, and again when it ends. Change or remove the date here any time.</p>}
            </div>

            <label className="block text-sm">
              <span className="text-gray-700 font-medium">Note</span><span className="text-gray-400"> — for you, e.g. why they're free</span>
              <input className="mt-1 w-full border border-gray-300 rounded px-3 py-2" value={form.note} onChange={(e) => set({ note: e.target.value })} placeholder="Partner account" />
            </label>

            {!confirming ? (
              <button className="px-4 py-2 bg-violet-600 text-white rounded-md text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
                disabled={problems.length > 0} onClick={() => setConfirming(true)}>
                {editing ? 'Save changes' : 'Set up Enterprise account'}
              </button>
            ) : (
              <div className="p-3 bg-white border border-violet-300 rounded-md text-sm space-y-2">
                <p className="text-gray-800">
                  <strong>{form.email}</strong> on <strong>{tier?.name || form.tierKey}</strong>, no monthly fee, {form.ends && form.endsOn ? <>ending <strong>{fmtDate(form.endsOn)}</strong></> : 'no end date'}
                  {parseInt(form.includedChecks, 10) > 0 ? `, ${num(parseInt(form.includedChecks, 10))} monitoring checks a month included` : ''}.
                  Monitoring add-on {form.monitoringCustom ? `${gbp(pounds(form.monitoringPounds))}/month` : 'normal price'} ·
                  job vacancies {form.jobsCustom ? `${gbp(pounds(form.jobsPounds))}/month` : 'normal price'} ·
                  contact credits {form.contactCustom ? `${form.contactPence}p each` : 'normal price'}.
                  {!editing && !form.fullName.trim() ? ' (They must already have an account. No email is sent.)' : ''}
                  {!editing && form.fullName.trim() ? ' If they have no account yet, one is created and they get a “set your password” email.' : ''}
                </p>
                <div className="flex gap-2">
                  <button className="px-4 py-1.5 bg-violet-600 text-white rounded-md font-medium hover:bg-violet-700 disabled:opacity-50" disabled={busy} onClick={save}>
                    {busy ? 'Saving…' : 'Confirm'}
                  </button>
                  <button className="px-4 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" onClick={() => setConfirming(false)}>Back</button>
                </div>
              </div>
            )}
            {problems.length > 0 && form.email && <p className="text-xs text-gray-500">Still needed: {problems.join(', ')}.</p>}
          </div>

          {/* Who's on it */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Enterprise accounts ({accounts.length})</h3>
            {accounts.length === 0 ? <p className="text-sm text-gray-500">None yet.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b">
                      <th className="py-2 pr-4">Account</th><th className="pr-4">Plan</th><th className="pr-4">Monitoring add-on</th>
                      <th className="pr-4">Job vacancies</th><th className="pr-4">Contact credits</th><th className="pr-4">Ends</th><th className="pr-4">Note</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((a) => {
                      const p = priceLine(a);
                      return (
                        <tr key={a.userId} className="border-b border-gray-100 align-top">
                          <td className="py-2 pr-4">
                            <div className="font-medium text-gray-900">{a.name || a.email}</div>
                            <div className="text-xs text-gray-500">{a.email}{a.teamSize > 1 ? ` · team of ${a.teamSize}` : ''}</div>
                          </td>
                          <td className="pr-4">{a.tierName || '—'}{a.includedMonitoringChecks > 0 && <div className="text-xs text-gray-500">{num(a.includedMonitoringChecks)} checks incl.</div>}
                            {a.planStatus && a.planStatus !== 'active' && <div className="text-xs text-red-600">{a.planStatus}</div>}</td>
                          <td className="pr-4 tabular-nums">{p.mon}</td>
                          <td className="pr-4 tabular-nums">{p.jobs}</td>
                          <td className="pr-4 tabular-nums">{p.con}</td>
                          <td className="pr-4 tabular-nums whitespace-nowrap">{a.planStatus && a.planStatus !== 'active'
                            ? <span className="text-red-600">Ended{a.endsAt ? ` ${fmtDate(a.endsAt)}` : ''}</span>
                            : a.endsAt ? fmtDate(a.endsAt) : <span className="text-gray-400">No end date</span>}</td>
                          <td className="pr-4 text-gray-500">{a.note || ''}</td>
                          <td className="text-right"><button className="text-blue-600 hover:text-blue-800" onClick={() => edit(a)}>Edit</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
